/**
 * Motivo por el que terminó la sesión, para explicárselo al usuario en la
 * pantalla de login (TALAPP-1862).
 *
 * Antes de este ticket, un usuario con la sesión caída aterrizaba en la
 * pantalla de login de KEYCLOAK, sin ninguna explicación y con otro branding.
 * Ahora cae en la pantalla propia, que le dice qué pasó.
 *
 * El motivo lo confirma el SERVER (`/api/auth/session-status`), que lo lee de
 * `user_sessions.revoked_reason` — el mismo dato que queda auditado. El
 * `?reason=` de la URL es solo una pista de la navegación y no se muestra tal
 * cual: sin confirmación del server no se muestra nada, para no inventarle al
 * usuario un motivo que no ocurrió.
 *
 * Un logout voluntario NO produce aviso: el usuario ya sabe que cerró sesión.
 *
 * El path es `/api/...` (raíz) también en compliance, siguiendo la convención
 * que ya usan su cliente tRPC (`/api/trpc`) y su propio login
 * (`/api/auth/login`). Detrás de nginx eso resuelve contra ava-client, que
 * comparte base, `JWT_SECRET` y cookie, así que la respuesta es la misma.
 */

import { useEffect, useState } from "react";

type SessionEndedKind = "idle" | "revoked";

const IDLE_REASONS = new Set(["IDLE_TIMEOUT", "ABSOLUTE_TIMEOUT"]);
const REVOKED_REASONS = new Set([
  "KEYCLOAK_BACKCHANNEL",
  "KEYCLOAK_INACTIVE",
  "ADMIN_REVOKE",
]);

export function useSessionEndedNotice(): SessionEndedKind | null {
  const [kind, setKind] = useState<SessionEndedKind | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Solo se pregunta si la navegación sugiere que hubo un corte. Entrar al
    // login por voluntad propia no dispara ninguna request extra.
    const params = new URLSearchParams(window.location.search);
    if (!params.has("reason")) return;

    (async () => {
      try {
        const res = await fetch("/api/auth/session-status", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as { endedReason?: string | null };
        if (cancelled || !data?.endedReason) return;

        if (IDLE_REASONS.has(data.endedReason)) setKind("idle");
        else if (REVOKED_REASONS.has(data.endedReason)) setKind("revoked");
        // USER_LOGOUT y NOT_FOUND: sin aviso, a propósito.
      } catch {
        // La pantalla de login nunca se rompe por esto.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return kind;
}
